import type { ColumnDef } from "@tanstack/react-table";
import { ChevronDown, ChevronRight, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { toggleRowExpanded, setRowsExpanded } from "@/lib/utils/tableExpansion";

type CreateExpandCollapseColumnOptions<T> = {
  /**
   * Only render the chevron for rows where this returns true.
   * Default: only top-level rows (`row.depth === 0`) that can expand.
   */
  showForRow?: (args: { row: any; original: T }) => boolean;
  /**
   * Width of the left expand column.
   */
  widthPx?: number;
};

/**
 * Generic leftmost expand/collapse column for TanStack Table.
 *
 * Works for any doctype/list as long as the table is configured with `getSubRows`
 * and an `onExpandedChange` handler (controlled or uncontrolled).
 */
export function createExpandCollapseColumn<T>(
  opts: CreateExpandCollapseColumnOptions<T> = {},
): ColumnDef<T, any> {
  const widthPx = opts.widthPx ?? 40;

  return {
    id: "__expand",
    header: ({ table }) => {
      const expandableRows = table.getRowModel().rows.filter((r) => r.getCanExpand());
      if (expandableRows.length === 0) {
        return <div className="flex items-center justify-center w-full" />;
      }
      const someExpanded = expandableRows.some((r) => r.getIsExpanded());
      const handleToggle = () => {
        const ids = expandableRows.map((r) => r.id);
        setRowsExpanded(table, ids, !someExpanded);
      };
      return (
        <div
          className="flex items-center justify-center w-full cursor-pointer hover:bg-accent/30 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              e.preventDefault();
              handleToggle();
            }
          }}
          aria-label={someExpanded ? "Collapse all" : "Expand all"}
          title={someExpanded ? "Collapse all" : "Expand all"}
        >
          {someExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </div>
      );
    },
    cell: ({ row, table }) => {
      const isTopLevel = row.depth === 0;
      const canExpand = row.getCanExpand();
      const isExpanded = row.getIsExpanded();
      const original = row.original as T;

      const shouldShow =
        opts.showForRow?.({ row, original }) ?? (isTopLevel && canExpand);

      // Child row: render tree connector line ⎿
      // Uses h-full with td { height: 0 } trick so lines span full row height
      if (!shouldShow && row.depth > 0) {
        const parentRow = row.getParentRow();
        const siblings = parentRow?.subRows ?? [];
        const isLast = siblings.length > 0 && siblings[siblings.length - 1].id === row.id;

        return (
          <div className="relative h-full w-full" aria-hidden="true">
            {/* Vertical line: top to center (last) or top to bottom (middle) */}
            <div
              className="absolute bg-neutral-300 dark:bg-neutral-600"
              style={{
                left: "50%",
                top: 0,
                bottom: isLast ? "50%" : 0,
                width: 1,
              }}
            />
            {/* Horizontal line: center-Y to right edge */}
            <div
              className="absolute bg-neutral-300 dark:bg-neutral-600"
              style={{
                left: "50%",
                right: 0,
                top: "50%",
                height: 1,
              }}
            />
          </div>
        );
      }

      if (!shouldShow) {
        return null;
      }

      return (
        <div className="relative h-full w-full">
          {/* Vertical line from chevron center down to bottom — renders behind chevron */}
          {isExpanded && (
            <div
              className="absolute bg-neutral-300 dark:bg-neutral-600 pointer-events-none"
              style={{
                left: "50%",
                top: "50%",
                bottom: 0,
                width: 1,
              }}
            />
          )}
          {/* Clickable chevron centered in the cell — on top of the line */}
          <div
            className={cn(
              "relative z-10 flex items-center justify-center",
              "w-full h-full cursor-pointer",
              "hover:bg-accent/30 transition-colors"
            )}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              toggleRowExpanded(table, row);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                e.preventDefault();
                toggleRowExpanded(table, row);
              }
            }}
            aria-label={isExpanded ? "Collapse" : "Expand"}
            aria-expanded={isExpanded}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>
      );
    },
    enableSorting: false,
    enableColumnFilter: false,
    enableResizing: false,
    size: widthPx,
    minSize: widthPx,
    maxSize: widthPx,
    meta: { noPadding: true },
  };
}

